import type { Socket } from 'socket.io-client'
import {
  SOCKET_EVENTS,
  type GameEventsPayload,
  type GameOverPayload,
  type GameStatePayload,
  type PlayerAction,
  type Seat,
  type DuelClockPayload,
  type GyGlancePayload,
  type SpectateListPayload,
} from '@shared/index'
import type { GameClient, GameClientHandlers } from './GameClient'
import { createAppSocket } from './createAppSocket'
import { publishClientRename, readAccountRename } from '../../account/liveRename'
import { releasePresenceHold } from './presenceHold'
import { isSocketTransportError } from './socketLink'
import { readAdminKey } from '../../views/admin/path'

/**
 * Read-only PvP board. Seat 0 is the bottom of the screen.
 * Plays are dropped here and again on the server.
 * `admin` sends the ops key so the server keeps both hands visible.
 */
export class SpectateGameClient implements GameClient {
  private readonly socket: Socket
  private readonly handlerSets: GameClientHandlers[] = []
  private readonly code: string
  private readonly admin: boolean
  private disposed = false
  readonly seat: Seat = 0
  readonly watch = true
  get roomCode(): string {
    return this.code
  }

  constructor(code: string, opts?: { admin?: boolean }) {
    this.code = code.trim().toUpperCase()
    this.admin = opts?.admin === true
    this.socket = createAppSocket()
    this.registerListeners()

    this.socket.on('connect_error', (err: Error) => {
      const message = err.message || 'connect_error'
      if (isSocketTransportError(message)) {
        this.fire('onLink', false)
        return
      }
      this.fire('onError', message)
    })

    this.socket.on('disconnect', (reason: string) => {
      if (this.disposed) return
      if (reason === 'io client disconnect') return
      this.fire('onLink', false)
    })

    this.socket.on('connect', () => {
      if (this.disposed) return
      this.fire('onLink', true)
      this.emitJoin()
    })
  }

  setHandlers(handlers: GameClientHandlers): void {
    this.handlerSets.push(handlers)
  }

  dropHandlers(): void {
    this.handlerSets.length = 0
  }

  private fire<K extends keyof GameClientHandlers>(
    key: K,
    ...args: Parameters<NonNullable<GameClientHandlers[K]>>
  ): void {
    for (const set of this.handlerSets) {
      const fn = set[key] as ((...a: typeof args) => void) | undefined
      fn?.(...args)
    }
  }

  send(_action: PlayerAction): void {
    /* watch only */
  }

  requestState(): void {
    if (!this.socket.connected) return
    this.emitJoin()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    releasePresenceHold(this.socket)
    const socket = this.socket
    socket.io.reconnection(false)
    let closed = false
    const close = () => {
      if (closed) return
      closed = true
      socket.removeAllListeners()
      socket.disconnect()
    }
    socket.timeout(800).emit(SOCKET_EVENTS.SPECTATE_LEAVE, () => close())
  }

  private emitJoin(): void {
    const key = this.admin ? readAdminKey() : ''
    this.socket.emit(SOCKET_EVENTS.SPECTATE_JOIN, {
      code: this.code,
      ...(key ? { key } : {}),
    })
  }

  private registerListeners(): void {
    this.socket.on(SOCKET_EVENTS.GAME_STATE, (p: GameStatePayload) => {
      this.fire('onState', p)
    })
    this.socket.on(SOCKET_EVENTS.ACCOUNT_RENAME, (payload: unknown) => {
      const swap = readAccountRename(payload)
      if (!swap) return
      publishClientRename(swap.previous, swap.username)
    })
    this.socket.on(SOCKET_EVENTS.GAME_EVENTS, (p: GameEventsPayload) => {
      this.fire('onEvents', p)
    })
    this.socket.on(SOCKET_EVENTS.GAME_OVER, (p: GameOverPayload) => {
      this.fire('onOver', p)
    })
    this.socket.on(SOCKET_EVENTS.GAME_CLOCK, (p: DuelClockPayload) => {
      this.fire('onClock', p)
    })
    this.socket.on(SOCKET_EVENTS.GY_GLANCE, (p: GyGlancePayload) => {
      this.fire('onGyGlance', p)
    })
    this.socket.on(SOCKET_EVENTS.SPECTATE_LIST, (p: SpectateListPayload) => {
      this.fire('onSpectators', p)
    })
    this.socket.on(SOCKET_EVENTS.ROOM_ERROR, (p: { message: string; code?: string }) => {
      this.fire('onError', p.message, p.code)
    })
    this.socket.on(SOCKET_EVENTS.GAME_ERROR, (p: { message: string; code?: string }) => {
      this.fire('onError', p.message, p.code)
    })
  }
}
